import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { useReveal } from "@/lib/useReveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  project_type: "",
  message: "",
};

export const Contact = () => {
  const ref = useReveal();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a brief message.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/contact`, payload);
      toast.success("Enquiry received — we'll be in touch.");
      setForm(empty);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="dark-section relative py-24 lg:py-32 bg-[#0A0A0A] text-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-16">
          {/* Left column */}
          <div className="col-span-12 lg:col-span-5 reveal">
            <div className="overline text-white/55">07 — Start a project</div>
            <h2
              data-testid="contact-heading"
              className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.02]"
            >
              Tell us about
              <br />
              the space.
            </h2>
            <p className="mt-6 max-w-md text-white/65 leading-relaxed">
              Briefs, drawings, a paragraph on a napkin — we'll come back within
              two working days with a feasibility view.
            </p>

            <div className="mt-12 space-y-5 text-sm">
              <div className="flex items-start gap-3" data-testid="contact-location">
                <MapPin size={16} className="mt-0.5 opacity-70" />
                <div>
                  <div className="overline text-white/55">Where</div>
                  <div className="mt-1">Scotland · UK-wide delivery</div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="contact-email">
                <Mail size={16} className="mt-0.5 opacity-70" />
                <div>
                  <div className="overline text-white/55">Email</div>
                  <a
                    href="mailto:hello@anderstongroup.co.uk"
                    className="mt-1 inline-block underline-offset-4 hover:underline"
                  >
                    hello@anderstongroup.co.uk
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="contact-phone">
                <Phone size={16} className="mt-0.5 opacity-70" />
                <div>
                  <div className="overline text-white/55">Telephone</div>
                  <div className="mt-1">By arrangement</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="col-span-12 lg:col-span-7 reveal"
          >
            <div className="grid grid-cols-12 gap-px bg-white/10">
              <Field
                label="Name"
                required
                col="col-span-12 sm:col-span-6"
                input={
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="bare-input"
                  />
                }
              />
              <Field
                label="Company"
                col="col-span-12 sm:col-span-6"
                input={
                  <input
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Company / practice"
                    className="bare-input"
                  />
                }
              />
              <Field
                label="Email"
                required
                col="col-span-12 sm:col-span-6"
                input={
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className="bare-input"
                  />
                }
              />
              <Field
                label="Phone"
                col="col-span-12 sm:col-span-6"
                input={
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+44 ..."
                    className="bare-input"
                  />
                }
              />
              <Field
                label="Project type"
                col="col-span-12"
                input={
                  <Select
                    value={form.project_type}
                    onValueChange={update("project_type")}
                  >
                    <SelectTrigger
                      data-testid="contact-select-project-type"
                      className="bg-transparent border-0 rounded-none text-white px-0 h-auto py-2 focus:ring-0 focus:ring-offset-0 shadow-none"
                    >
                      <SelectValue placeholder="Select a sector" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] text-white border-white/15 rounded-none">
                      {[
                        "Workspace",
                        "Bar",
                        "Restaurant",
                        "Distillery",
                        "Designed Interior",
                        "Other",
                      ].map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          className="rounded-none focus:bg-white/10 focus:text-white"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
              />
              <Field
                label="Brief"
                required
                col="col-span-12"
                input={
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Scope, location, programme, budget — anything you can share."
                    rows={5}
                    className="bare-input resize-none"
                  />
                }
              />
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <div className="text-xs text-white/45 max-w-md">
                By submitting, you agree we may contact you about your enquiry.
                We don't share details with third parties.
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 px-7 py-4 text-sm font-medium bg-white text-[#0A0A0A] hover:bg-white/85 disabled:opacity-60 transition-colors"
              >
                {loading ? "Sending…" : "Send enquiry"} <ArrowUpRight size={16} />
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .bare-input{
          width:100%;
          background:transparent;
          color:#fff;
          border:0;
          outline:0;
          padding:.4rem 0;
          font-size:1rem;
          font-family: inherit;
        }
        .bare-input::placeholder{ color: rgba(255,255,255,.35); }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, col, input }) => (
  <label className={`${col} bg-[#0A0A0A] p-6 lg:p-7 flex flex-col gap-2`}>
    <span className="overline text-white/55">
      {label} {required && <span className="text-white/45">*</span>}
    </span>
    {input}
  </label>
);

export default Contact;
